import axiosInstance from "../api/axiosInstance";

// Superdoc memory: what the assistant remembers about a clinician and their
// practice. `user` / `user_patient` entries belong to the caller; `clinic`
// entries are shared and editable only when the backend says `can_edit_clinic`.
export type MemoryScope = "clinic" | "user" | "user_patient";
export type MemoryOrigin = "explicit" | "inferred" | "operator";
export type MemoryStatus = "active" | "pending" | "retired";

export interface MemoryEntryDto {
  id: string;
  scope: MemoryScope;
  category: string;
  category_label: string;
  statement: string;
  binding: Record<string, unknown> | null;
  origin: MemoryOrigin;
  status: MemoryStatus;
  sensitivity: string;
  client_id: number | null;
  group_id: number | null;
  replaces_id: string | null;
  created_at: string;
  retired_at: string | null;
  retired_reason: string;
  undo_until: string | null;
  attributes: Record<string, unknown>;
}

export interface MemoryEntriesResponse {
  entries: MemoryEntryDto[];
  can_edit_clinic: boolean;
}

export interface MemoryListParams {
  scope?: string;
  status?: string;
}

export const CLINIC_MEMORY_CATEGORIES: { value: string; label: string }[] = [
  { value: "orders.lab_order_set", label: "Lab order set" },
  { value: "practice.fact", label: "Practice fact" },
  { value: "standing.instruction", label: "Standing instruction" },
];

const BASE = "/superdoc/memory";

export const memoryService = {
  listEntries: async (params?: MemoryListParams): Promise<MemoryEntriesResponse> => {
    const response = await axiosInstance.get<MemoryEntriesResponse>(`${BASE}/entries/`, { params });
    return response.data;
  },

  editEntry: async (id: string, statement: string): Promise<MemoryEntryDto> => {
    const response = await axiosInstance.patch<MemoryEntryDto>(`${BASE}/entries/${id}/`, { statement });
    return response.data;
  },

  confirmEntry: async (id: string): Promise<MemoryEntryDto> => {
    const response = await axiosInstance.post<MemoryEntryDto>(`${BASE}/entries/${id}/confirm/`);
    return response.data;
  },

  dismissEntry: async (id: string): Promise<MemoryEntryDto> => {
    const response = await axiosInstance.post<MemoryEntryDto>(`${BASE}/entries/${id}/retire/`, {
      reason: "dismissed",
    });
    return response.data;
  },

  deleteEntry: async (id: string): Promise<MemoryEntryDto> => {
    const response = await axiosInstance.post<MemoryEntryDto>(`${BASE}/entries/${id}/retire/`, {
      reason: "user_deleted",
    });
    return response.data;
  },

  undoEntry: async (id: string): Promise<MemoryEntryDto> => {
    const response = await axiosInstance.post<MemoryEntryDto>(`${BASE}/entries/${id}/undo/`);
    return response.data;
  },

  createClinicEntry: async (statement: string, category: string): Promise<MemoryEntryDto> => {
    const response = await axiosInstance.post<MemoryEntryDto>(`${BASE}/clinic/`, { statement, category });
    return response.data;
  },
};
